import {
  resetStreamingAiResponse,
  updateModalsCloseDrawerModal,
  updateSupportTicketInformation,
  updateSupportCommentsByTicket,
} from "@src/reducers/your-reducer";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector, useStore } from "react-redux";
import { RootState } from "@src/reducers/store";
import {
  postSupportCommentByTicketUseCase,
  postSupportCommentByTicketWithFilesUseCase,
} from "@src/cloud-connect/support/use-cases/post-support-comment-by-ticket-use-case";
import type { PostSupportCommentByTicketResult } from "@src/cloud-connect/support/use-cases/post-support-comment-by-ticket-use-case";
import type { CommentStreamEvent } from "@src/cloud-connect/api/support/post-support-comment-with-ai-stream";
import { postSupportCommentWithAIStreamUseCase } from "@src/cloud-connect/support/use-cases/post-support-comment-with-ai-stream-use-case";
import { isImageFile } from "@src/cloud-connect/support/utils/is-image-file";

import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import {
  GetSupportCommentsByTicketResponse,
  PostSupportCommentByTicketPayload,
  supportTicketByIdType,
  TicketCommentAttachment,
  TicketCommentItem,
} from "@src/types/support";
import { getSupportCommentsByTicketWithLanguageUseCase } from "@src/cloud-connect/support/use-cases/get-support-comments-by-ticket-with-language-use-case";
import { getSupportTicketByIdWithLanguageUseCase } from "@src/cloud-connect/support/use-cases/get-support-ticket-by-id-with-language-use-case";
import { getLanguage } from "@src/cloud-connect/utils/get-language";
import { patchTicketAssigneeUseCase } from "@src/cloud-connect/support/use-cases/patch-ticket-assignee-use-case";
import { patchTicketStatusUseCase } from "@src/cloud-connect/support/use-cases/patch-ticket-status-use-case";
import {
  StreamTool,
  completeRunningTools,
  mergeToolCall,
  normalizeToolCall,
} from "@src/components/molecules/tool-activity-section/stream-tools";
import { useI18N } from "@src/language/language-context";
import {
  clearStreamingTicketLocalFiles,
  getStreamingTicketLocalFiles,
} from "@src/cloud-connect/support/utils/streaming-ticket-local-files";

// Dedicated, ever-incrementing id source for locally-built attachments — kept
// separate from localIdRef (used for message ids) so every attachment across
// every send/ticket-creation flow gets a globally unique id, regardless of
// how many messages or sends happened in between. Prevents id collisions and
// removes any dependency on filename uniqueness for React keys/identity.
let localAttachmentIdCounter = 0;
const nextLocalAttachmentId = () => `local-att-${--localAttachmentIdCounter}`;

const buildLocalAttachments = (
  isAttachment: string | false | undefined,
  fileName: string | undefined,
  attachmentFile: File | undefined,
  attachedFiles: File[] = [],
): TicketCommentAttachment[] => {
  const attachments: TicketCommentAttachment[] = [];

  if (isAttachment) {
    attachments.push({
      id: nextLocalAttachmentId(),
      file_name: fileName || "attachment",
      content_url: "",
      local_file: attachmentFile,
      content_type: "",
      size: 0,
      inline: false,
    });
  }

  return attachments.concat(
    attachedFiles.map((file) => ({
      id: nextLocalAttachmentId(),
      file_name: file.name,
      content_url: "",
      local_file: file,
      content_type: file.type,
      size: file.size,
      inline: false,
    })),
  );
};

const mergeServerAttachments = (
  localAttachments: TicketCommentAttachment[],
  serverAttachments: TicketCommentAttachment[],
): TicketCommentAttachment[] => {
  const unmatchedLocal = [...localAttachments];

  for (const serverAttachment of serverAttachments) {
    const localIndex = unmatchedLocal.findIndex(
      (localAttachment) =>
        localAttachment.file_name === serverAttachment.file_name &&
        isImageFile(
          serverAttachment.file_name,
          serverAttachment.content_type ?? "",
        ) ===
          isImageFile(
            localAttachment.file_name,
            localAttachment.local_file?.type,
          ),
    );
    if (localIndex !== -1) {
      unmatchedLocal.splice(localIndex, 1);
    }
  }

  return [...unmatchedLocal, ...serverAttachments];
};

interface ShowLoaderType {
  showLoader?: boolean;
}

interface FetchResult<TData> {
  success: boolean;
  data?: TData;
  error?: string;
}

interface FetchConfig<TData> {
  useCaseFn: (
    ticketId: string,
    language: string,
  ) => Promise<FetchResult<TData>>;
  onSuccess: (data: TData) => void;
  showLoader?: boolean;
}

const EMPTY_COMMENTS: never[] = [];

export const useSupportChatHook = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const store = useStore<RootState>();

  const texts = {
    errorStreamAnswer: i18n("modules.supportChatModal.errorStreamAnswer"),
    handoverText: i18n("modules.supportChatModal.handoverText"),
    imagesOnlyForAIAssistant: i18n(
      "modules.supportChatModal.imagesOnlyForAIAssistant",
    ),
  };

  const ticketId = useSelector(
    (state: RootState) => state.yourSlice.supportTicketInformation.id,
  );
  const activeTicketIdRef = useRef<string | undefined>(ticketId);
  const ticketRequestGenerationRef = useRef(0);
  const ticketDataRequestRef = useRef(0);
  const commentsDataRequestRef = useRef(0);
  const pendingHandoffCommentFetchRef = useRef(false);
  const humanHandoffTicketRef = useRef<string | null>(null);
  const handoffCommentsFetchedTicketRef = useRef<string | null>(null);
  const handoffCommentsInFlightTicketRef = useRef<string | null>(null);
  activeTicketIdRef.current = ticketId;

  useEffect(() => {
    const effectTicketId = ticketId;
    activeTicketIdRef.current = effectTicketId;
    ticketRequestGenerationRef.current += 1;
    ticketDataRequestRef.current += 1;
    commentsDataRequestRef.current += 1;
    pendingHandoffCommentFetchRef.current = false;
    humanHandoffTicketRef.current = null;
    handoffCommentsFetchedTicketRef.current = null;
    handoffCommentsInFlightTicketRef.current = null;

    return () => {
      ticketRequestGenerationRef.current += 1;
      ticketDataRequestRef.current += 1;
      commentsDataRequestRef.current += 1;
      if (activeTicketIdRef.current === effectTicketId) {
        activeTicketIdRef.current = undefined;
      }
    };
  }, [ticketId]);

  const userId = useSelector(
    (state: RootState) => state.yourSlice.authContext.userId,
  );

  const fullName = useSelector(
    (state: RootState) => state.yourSlice.authContext?.fullName,
  );

  const language = useSelector((state: RootState) => state.yourSlice.language);

  const ticketInformation = useSelector(
    (state: RootState) => state.yourSlice.supportTicketInformation,
    shallowEqual,
  );

  const sseState = useSelector(
    (state: RootState) => state.sseSlice.streams?.["service-support"],
    shallowEqual,
  );
  const lastEvent = sseState?.lastEvent;
  const events = sseState?.events ?? {};

  const comments = useSelector(
    (state: RootState) =>
      state.yourSlice.supportCommentsByTicket?.comments ?? EMPTY_COMMENTS,
    shallowEqual,
  );

  // Ticket-creation stream state, written by useCreateSupportTicketStream.
  const streamingAiResponseText = useSelector(
    (state: RootState) => state.yourSlice.streamingAiResponseText,
  );
  const streamingAiResponseTools = useSelector(
    (state: RootState) => state.yourSlice.streamingAiResponseTools,
  );
  const isStreamingAiResponse = useSelector(
    (state: RootState) => state.yourSlice.isStreamingAiResponse,
  );
  const streamingTicketId = useSelector(
    (state: RootState) => state.yourSlice.streamingTicketId,
  );

  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const isSendingRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isWaitingForAIResponse, setIsWaitingForAIResponse] = useState(false);
  const [showHumanSupportButton, setShowHumanSupportButton] = useState(false);
  const [showMarkAsDoneButton, setShowMarkAsDoneButton] = useState(false);
  const [isPatchingAssignee, setIsPatchingAssignee] = useState(false);
  const [isPatchingStatus, setIsPatchingStatus] = useState(false);
  const isPatchingAssigneeRef = useRef(false);
  const isPatchingStatusRef = useRef(false);
  /** Sent by user but not yet fetched from API — shown locally until stream completes. */
  const localMessagesRef = useRef<TicketCommentItem[]>([]);
  const [localMessagesVersion, setLocalMessagesVersion] = useState(0);
  const [handoffFetchRetryVersion, setHandoffFetchRetryVersion] = useState(0);
  const updateLocalMessages = useCallback(
    (updater: (prev: TicketCommentItem[]) => TicketCommentItem[]) => {
      const nextMessages = updater(localMessagesRef.current);
      if (nextMessages === localMessagesRef.current) return;
      localMessagesRef.current = nextMessages;
      setLocalMessagesVersion((version) => version + 1);
    },
    [],
  );
  // Local message ids are string placeholders ("local-N") — persisted comment ids are UUIDs.
  const localIdRef = useRef(0);
  const nextLocalId = useCallback(() => `local-${--localIdRef.current}`, []);
  const streamingLocalIdRef = useRef<string | null>(null);
  /** Tracks whether the stream's "ready" event (POST acknowledged) has been received. */
  const readyReceivedRef = useRef(false);
  /** In-flight AI reply text, shown as a virtual comment bubble (id: -1) via displayComments. */
  const [streamingAiReplyText, setStreamingAiReplyText] = useState<
    string | null
  >(null);
  /** Tool-call activity for the in-flight AI bubble (cleared with the bubble). */
  const [streamingAiReplyTools, setStreamingAiReplyTools] = useState<
    StreamTool[]
  >([]);
  const streamingAiReplyTextRef = useRef<string | null>(null);
  const preventHumanButtonRef = useRef(false);
  const previousLanguageRef = useRef(language);
  /** Mirrors Redux comments so async callbacks (done handler, SSE) avoid stale closures. */
  const commentsRef = useRef(comments);
  /** Bumped per sendMessage call so a stale background callback can detect it's superseded. */
  const sendRequestIdRef = useRef(0);
  /**
   * Id of the last comment before this send. GET /comments can lag the SSE events, so a
   * fetch right after `ready` may still return the pre-send list — this flags that
   * trailing bot comment as stale (last exchange's), not this one.
   */
  const lastCommentIdBeforeSendRef = useRef<string | null>(null);
  // Refs mirror Redux stream fields so ticketId mount / fetch callbacks see latest values
  // without re-running effects on every token.
  const streamingTicketIdRef = useRef(streamingTicketId);
  const isStreamingAiResponseRef = useRef(isStreamingAiResponse);
  const streamingAiResponseTextRef = useRef(streamingAiResponseText);
  const streamingAiResponseToolsRef = useRef(streamingAiResponseTools);
  /** True while ticket-creation SSE is open — used to skip service-support SSE refetches. */
  const isTicketCreationStreamInProgressRef = useRef(false);
  /** Tracks ticket-creation stream so we can finalize when isStreamingAiResponse flips false. */
  const wasTicketCreationStreamingRef = useRef(false);

  const isAIRagLLMBot = ticketInformation?.handled_by_ai_chatbot === true;
  const isAIRagLLMBotRef = useRef(isAIRagLLMBot);
  const sentUserLocalIdRef = useRef<string | null>(null);
  const ticketInformationRef = useRef(ticketInformation);
  useEffect(() => {
    isAIRagLLMBotRef.current = isAIRagLLMBot;
    ticketInformationRef.current = ticketInformation;
  }, [isAIRagLLMBot, ticketInformation]);

  useEffect(() => {
    commentsRef.current = comments;
  }, [comments]);

  useEffect(() => {
    streamingTicketIdRef.current = streamingTicketId;
    isStreamingAiResponseRef.current = isStreamingAiResponse;
    streamingAiResponseTextRef.current = streamingAiResponseText;
    streamingAiResponseToolsRef.current = streamingAiResponseTools;
    isTicketCreationStreamInProgressRef.current =
      ticketId != null &&
      streamingTicketId === ticketId &&
      isStreamingAiResponse;
  }, [
    ticketId,
    streamingTicketId,
    isStreamingAiResponse,
    streamingAiResponseText,
    streamingAiResponseTools,
  ]);

  const updateStreamingAiReplyText = useCallback((value: string | null) => {
    streamingAiReplyTextRef.current = value;
    setStreamingAiReplyText(value);
  }, []);

  /** Clears only the virtual text bubble — keeps completed tool activity for the bot message (like AI RAG). */
  const clearStreamingBubble = useCallback(() => {
    updateStreamingAiReplyText(null);
  }, [updateStreamingAiReplyText]);

  /** Full reset of in-flight AI reply UI (new send, close modal, ticket change). */
  const clearStreamingAiReply = useCallback(() => {
    clearStreamingBubble();
    setStreamingAiReplyTools([]);
  }, [clearStreamingBubble]);

  const resetCompletedTicketCreationStream = useCallback(() => {
    const streamState = store.getState().yourSlice;
    if (
      ticketId != null &&
      streamState.streamingTicketId === ticketId &&
      !streamState.isStreamingAiResponse
    ) {
      dispatch(resetStreamingAiResponse());
    }
  }, [dispatch, store, ticketId]);

  /** Clears the ticket-creation streaming bubble and its completed Redux state. */
  const finalizeBubbleIfReplyPersisted = useCallback(
    (shouldResetTicketCreationStream: boolean) => {
      clearStreamingBubble();
      if (shouldResetTicketCreationStream) {
        resetCompletedTicketCreationStream();
      }
    },
    [clearStreamingBubble, resetCompletedTicketCreationStream],
  );

  const displayComments = useMemo(() => {
    const result: TicketCommentItem[] = [...comments];

    // Normalize content to handle API formatting differences (HTML wrapping,
    // unicode escapes, URL encoding) that would cause the raw content to differ
    // from the streamed plain text — preventing reconciliation and producing
    // duplicate bot messages.
    const normalizeContent = (s: string): string => {
      let v = s;
      try {
        v = decodeURIComponent(v);
      } catch {
        /* not URL-encoded */
      }
      v = v
        .replace(/\\u003c/gi, "<")
        .replace(/\\u003e/gi, ">")
        .replace(/\\u0026/gi, "&")
        .replace(/\\u0022/gi, '"')
        .replace(/\\u0027/gi, "'")
        .replace(/\\"/g, '"')
        .replace(/\\n/g, "\n");
      // Unwrap JSON-quoted content (e.g. `"Hello"` → `Hello`).
      const trimmed = v.trim();
      if (
        (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
        (trimmed.startsWith("'") && trimmed.endsWith("'"))
      ) {
        try {
          v = JSON.parse(trimmed);
        } catch {
          /* keep original */
        }
      }
      // Decode all HTML entities (textarea handles &amp; &lt; &gt; &quot; etc.).
      for (let i = 0; i < 5; i += 1) {
        const textarea = document.createElement("textarea");
        textarea.innerHTML = v;
        const next = textarea.value;
        if (next === v) break;
        v = next;
      }
      return v.replace(/<[^>]*>/g, "").trim();
    };
    const localMessages = localMessagesRef.current;
    if (localMessages.length > 0) {
      // Count real comments by (author, normalized content) — count-based matching
      // prevents one confirmed comment from hiding multiple identical local messages.
      const remaining = new Map<string, TicketCommentItem[]>();
      for (const c of comments) {
        // key on the AI flag (not author id): persisted bot comments are
        // only reconcilable via the server-provided is_ai_chatbot_comment flag
        const key = JSON.stringify([
          c.is_ai_chatbot_comment === true,
          normalizeContent(c.content),
        ]);
        const bucket = remaining.get(key);
        if (bucket) {
          bucket.push(c);
        } else {
          remaining.set(key, [c]);
        }
      }
      for (const msg of localMessages) {
        const key = JSON.stringify([
          msg.is_ai_chatbot_comment === true,
          normalizeContent(msg.content),
        ]);
        const bucket = remaining.get(key) ?? [];
        const persistedIndex =
          msg.content || !msg.attachments?.length
            ? 0
            : bucket.findIndex((candidate) =>
                candidate.attachments?.some((serverAttachment) =>
                  msg.attachments?.some(
                    (localAttachment) =>
                      localAttachment.file_name === serverAttachment.file_name,
                  ),
                ),
              );
        const persisted =
          persistedIndex >= 0 ? bucket.splice(persistedIndex, 1)[0] : undefined;
        if (!persisted) {
          result.push(msg);
          continue;
        }
        remaining.set(key, bucket);
        // The persisted comment may lack regular file attachments the local
        // optimistic version still holds via local_file (the backend can skip
        // them when their upload token didn't resolve). Merge local-only
        // attachments into the persisted comment instead of dropping them.
        if (msg.attachments?.length) {
          const idx = result.indexOf(persisted);
          if (idx !== -1) {
            result[idx] = {
              ...persisted,
              attachments: mergeServerAttachments(
                msg.attachments,
                persisted.attachments ?? [],
              ),
            };
          }
        }
      }
    }

    // — Legacy ticket-creation streaming bubble —
    // Renders the AI greeting bubble during active ticket-creation streaming.
    // isActiveStream gates removal of the last bot comment: only replace it
    // while the stream is live. When streamingAiReplyText is leftover from
    // a completed stream (isStreamingAiResponse / isWaitingForAIResponse
    // are both false), the persisted bot comment is authoritative — don't
    // remove it and don't push a stale virtual bubble over it.
    if (
      streamingLocalIdRef.current === null &&
      (streamingAiReplyText || isWaitingForAIResponse)
    ) {
      const lastComment = comments[comments.length - 1];
      const isLastFromBot =
        lastComment?.is_ai_chatbot_comment === true &&
        lastComment.id !== lastCommentIdBeforeSendRef.current;
      const isActiveStream = isStreamingAiResponse || isWaitingForAIResponse;

      if (isLastFromBot && isActiveStream) {
        const idx = result.findIndex((c) => c.id === lastComment.id);
        if (idx !== -1) result.splice(idx, 1);
      }

      // Only push the virtual bubble when there is no persisted bot
      // comment to conflict with, or during an active stream where
      // we just removed the stale persisted entry above.
      if (!isLastFromBot || isActiveStream) {
        // Safety net: if a persisted bot comment with the same normalized
        // content already exists in the result, the local bot bubble wasn't
        // reconciled — skip the duplicate. Persisted API comments are never
        // removed; the is_ai_chatbot_comment flag identifies bot comments.
        const isDuplicate = result.some(
          (existing) =>
            existing.is_ai_chatbot_comment &&
            normalizeContent(existing.content) ===
              normalizeContent(streamingAiReplyText ?? ""),
        );
        if (!isDuplicate) {
          result.push({
            // synthetic local bubble: id is display-only (React key), never persisted
            id: isLastFromBot ? lastComment.id : "virtual-bot-comment",
            created_at: isLastFromBot
              ? lastComment.created_at
              : new Date().toISOString(),
            content: streamingAiReplyText ?? "",
            is_ai_chatbot_comment: true,
            author: {
              type: "SUPPORT_AGENT",
              id: "",
              name: "AI Assistant",
              email: null,
              organization: null,
            },
          } as TicketCommentItem);
        }
      }
    }

    return result;
  }, [
    comments,
    localMessagesVersion,
    streamingAiReplyText,
    isWaitingForAIResponse,
    userId,
  ]);

  const fetchData = useCallback(
    async <TData,>(config: FetchConfig<TData>): Promise<boolean> => {
      const requestTicketId = ticketId;
      const requestGeneration = ticketRequestGenerationRef.current;
      const isCurrentRequest = () =>
        activeTicketIdRef.current === requestTicketId &&
        ticketRequestGenerationRef.current === requestGeneration;

      if (!requestTicketId || !isCurrentRequest()) {
        return false;
      }

      if (config.showLoader) {
        setIsLoading(true);
      }

      try {
        const result = await config.useCaseFn(requestTicketId, getLanguage());
        if (!isCurrentRequest()) return false;

        if (!result.success) {
          errorSnackbar(result.error);
          return true;
        }

        config.onSuccess(result.data);
        return true;
      } catch {
        // errors already handled via errorSnackbar above
        return isCurrentRequest();
      }
    },
    [ticketId],
  );

  const patchTicketAssignee = async (ticketId: string) => {
    setIsPatchingAssignee(true);

    if (isPatchingAssigneeRef.current) return;

    isPatchingAssigneeRef.current = true;
    try {
      // handover = unassign AND clear the AI flag (the flag is
      // the authoritative signal server-side, the unassign is cosmetic)
      const response = await patchTicketAssigneeUseCase(
        ticketId,
        null,
        texts.handoverText,
        { handledByAiChatbot: false },
      );

      if (!response.success) {
        errorSnackbar(response.error);
        return;
      }

      setShowHumanSupportButton(false);
      preventHumanButtonRef.current = true;
      humanHandoffTicketRef.current = ticketId;
      // Sync ref immediately so the SSE handler sees the new assignee on the
      // next event (ticket_updated) rather than the stale bot assignment.
      ticketInformationRef.current = {
        ...ticketInformationRef.current,
        assigned_to: null,
        handled_by_ai_chatbot: false,
      } as supportTicketByIdType;
      // fetchCommentsData's onSuccess clears local messages once fresh
      // API data is in Redux — don't clear here or the UI shows a gap.
      // Fetch fresh data directly regardless of SSE timing — ensures the UI
      // syncs even if the SSE ticket_updated arrives before the ref update
      // or is race-dropped by the bot-assigned guards.
      fetchHandoffCommentsOnce(ticketId);
      void fetchTicketData();
    } finally {
      isPatchingAssigneeRef.current = false;
      setIsPatchingAssignee(false);
    }
  };

  const patchTicketMarkAsDone = async (ticketId: string) => {
    if (isPatchingStatusRef.current) return;

    isPatchingStatusRef.current = true;
    setIsPatchingStatus(true);

    try {
      const response = await patchTicketStatusUseCase(ticketId, "solved");

      if (!response.success) {
        errorSnackbar(response.error);
        return;
      }

      setShowMarkAsDoneButton(false);
      setShowHumanSupportButton(false);
      ticketInformationRef.current = {
        ...ticketInformationRef.current,
        status: "solved",
      } as supportTicketByIdType;
    } finally {
      isPatchingStatusRef.current = false;
      setIsPatchingStatus(false);
    }
  };

  const fetchTicketData = useCallback(
    async ({ showLoader }: ShowLoaderType = {}) => {
      const requestId = ++ticketDataRequestRef.current;
      const isCurrentRequest = await fetchData({
        useCaseFn: getSupportTicketByIdWithLanguageUseCase,
        onSuccess: (data: supportTicketByIdType) => {
          if (requestId === ticketDataRequestRef.current) {
            dispatch(updateSupportTicketInformation(data));
          }
        },
        showLoader,
      });
      return isCurrentRequest && requestId === ticketDataRequestRef.current;
    },
    [dispatch, fetchData],
  );

  const hasBotReachedTwoCommentsChecker = (
    arr: TicketCommentItem[],
    handledByAi: boolean,
  ) => {
    if (!handledByAi) return false;
    // the bot must have written at least two comments before the
    // Mark as Done / Human Support actions become available
    let count = 0;
    for (const obj of arr) {
      if (obj.is_ai_chatbot_comment === true) {
        count++;
        if (count >= 2) {
          return true;
        }
      }
    }
    return false;
  };

  const fetchCommentsData = useCallback(
    async ({
      showLoader,
      preserveLocalMessages = false,
      onFetchSuccess,
    }: ShowLoaderType & {
      preserveLocalMessages?: boolean;
      onFetchSuccess?: () => void;
    } = {}) => {
      const requestId = ++commentsDataRequestRef.current;
      const streamingStateAtRequest = store.getState().yourSlice;
      const isHandoffRequestAtStart =
        humanHandoffTicketRef.current === ticketId;
      const isCommentsRequestEligible =
        !isSendingRef.current &&
        !(
          streamingStateAtRequest.streamingTicketId === ticketId &&
          streamingStateAtRequest.isStreamingAiResponse
        ) &&
        streamingLocalIdRef.current === null;
      const isCurrentRequest = await fetchData({
        useCaseFn: getSupportCommentsByTicketWithLanguageUseCase,
        onSuccess: (data: GetSupportCommentsByTicketResponse) => {
          if (requestId !== commentsDataRequestRef.current) return;
          const streamingState = store.getState().yourSlice;
          const isTicketCreationStreamActive =
            streamingState.streamingTicketId === ticketId &&
            streamingState.isStreamingAiResponse;
          const isHandoffRequest = humanHandoffTicketRef.current === ticketId;
          const isCurrentlyBusy =
            isSendingRef.current ||
            isTicketCreationStreamActive ||
            streamingLocalIdRef.current !== null;
          if (
            !isCommentsRequestEligible ||
            isCurrentlyBusy ||
            (isHandoffRequest && !isHandoffRequestAtStart)
          ) {
            if (isHandoffRequest) {
              pendingHandoffCommentFetchRef.current = true;
              if (!isCurrentlyBusy) {
                setHandoffFetchRetryVersion((version) => version + 1);
              }
            }
            return;
          }

          dispatch(updateSupportCommentsByTicket(data));
          if (isHandoffRequest) {
            handoffCommentsFetchedTicketRef.current = ticketId;
            pendingHandoffCommentFetchRef.current = false;
            updateLocalMessages(() => []);
            sentUserLocalIdRef.current = null;
            clearStreamingAiReply();
            clearStreamingTicketLocalFiles(ticketId);
            setIsWaitingForAIResponse(false);
            resetCompletedTicketCreationStream();
          }
          onFetchSuccess?.();
          const count = data?.comments?.length ?? 0;
          const hasBotReachedTwoComments = hasBotReachedTwoCommentsChecker(
            data?.comments ?? [],
            ticketInformationRef.current?.handled_by_ai_chatbot === true,
          );
          const ticket = ticketInformationRef.current;
          const isBot = ticket?.handled_by_ai_chatbot === true;
          // Read auto_translate from the store (not the ref) because
          // the ref is synced by a useEffect that hasn't run yet —
          // all of this executes inside a microtask before React renders.
          const autoTranslate =
            store.getState().yourSlice.supportTicketInformation?.auto_translate;

          // When auto-translation produced translated comments, clear local
          // messages so optimistic entries (which hold untranslated content)
          // don't duplicate alongside the authoritative translated versions
          // from the API. Otherwise local messages are kept: displayComments
          // reconciles them against persisted comments (flag + normalized
          // content, count-based) and merges local-only image attachments, so
          // wiping here would drop the optimistic image previews.
          if (autoTranslate && !preserveLocalMessages) {
            updateLocalMessages(() => []);
          }

          // Clear leftover streaming text (e.g. AI greeting from ticket
          // creation). Otherwise sendMessage captures it as a stale
          // local message on the next user send, producing duplicates.
          if (
            streamingLocalIdRef.current === null &&
            !isTicketCreationStreamActive &&
            !isStreamingAiResponseRef.current &&
            (humanHandoffTicketRef.current !== ticketId ||
              !isSendingRef.current)
          ) {
            updateStreamingAiReplyText(null);
          }

          // isWaitingForAIResponse is only ever set by an active send/stream, never
          // inferred here — otherwise a ticket with an unanswered message would show a
          // permanent "Thinking..." on reopen.

          // Human Support / Mark as Done only after enough persisted comments.
          if (
            count >= 4 &&
            hasBotReachedTwoComments &&
            ticket?.status !== "solved"
          ) {
            if (isBot && !preventHumanButtonRef.current) {
              setShowHumanSupportButton(true);
            }
            setShowMarkAsDoneButton(true);
          }
        },
        showLoader,
      });
      return isCurrentRequest && requestId === commentsDataRequestRef.current;
    },
    [
      clearStreamingAiReply,
      dispatch,
      fetchData,
      resetCompletedTicketCreationStream,
      store,
      ticketId,
      updateLocalMessages,
    ],
  );

  const fetchHandoffCommentsOnce = useCallback(
    (requestTicketId: string) => {
      if (
        handoffCommentsFetchedTicketRef.current === requestTicketId ||
        handoffCommentsInFlightTicketRef.current === requestTicketId
      ) {
        return;
      }
      const streamingState = store.getState().yourSlice;
      if (
        isSendingRef.current ||
        isTicketCreationStreamInProgressRef.current ||
        (streamingState.streamingTicketId === requestTicketId &&
          streamingState.isStreamingAiResponse) ||
        streamingLocalIdRef.current !== null
      ) {
        pendingHandoffCommentFetchRef.current = true;
        return;
      }
      if (handoffCommentsInFlightTicketRef.current === requestTicketId) {
        pendingHandoffCommentFetchRef.current = true;
        return;
      }
      pendingHandoffCommentFetchRef.current = false;
      handoffCommentsInFlightTicketRef.current = requestTicketId;
      void fetchCommentsData()
        .then((isCurrentRequest) => {
          if (
            !isCurrentRequest &&
            humanHandoffTicketRef.current === requestTicketId &&
            handoffCommentsFetchedTicketRef.current !== requestTicketId
          ) {
            pendingHandoffCommentFetchRef.current = true;
          }
        })
        .finally(() => {
          if (handoffCommentsInFlightTicketRef.current === requestTicketId) {
            handoffCommentsInFlightTicketRef.current = null;
          }
          if (
            pendingHandoffCommentFetchRef.current &&
            activeTicketIdRef.current === requestTicketId
          ) {
            setHandoffFetchRetryVersion((version) => version + 1);
          }
        });
    },
    [fetchCommentsData, store],
  );

  useEffect(() => {
    if (
      lastEvent !== "ticket_updated" &&
      lastEvent !== "ticket_created" &&
      lastEvent !== "comment_created" &&
      lastEvent !== "ticket_ai_stream_saved"
    ) {
      return;
    }

    const eventPayload = events?.[lastEvent];
    const eventTicketId = eventPayload?.ticket_id;
    if (eventTicketId === null || eventTicketId !== ticketId) {
      return;
    }

    const isBot = ticketInformationRef.current?.handled_by_ai_chatbot === true;

    // While bot is assigned, keep all messages local — skip comment_created
    // refetches. ticket_ai_stream_saved passes through as a safety net: the
    // per-comment SSE stream may have terminated without a visible answer
    // (e.g. the server answered 201 JSON with only a "ready" event), so the
    // persisted bot reply must still be fetched; displayComments reconciliation
    // hides the local bubbles once persisted comments arrive.
    // ticket_updated/ticket_created pass through for ticket-info sync.
    if (
      isBot &&
      lastEvent !== "ticket_updated" &&
      lastEvent !== "ticket_created" &&
      lastEvent !== "ticket_ai_stream_saved"
    ) {
      return;
    }

    if (lastEvent === "ticket_updated" || lastEvent === "ticket_created") {
      const wasBotAssigned = isBot;
      void fetchTicketData().then((isCurrentRequest) => {
        if (!isCurrentRequest || !wasBotAssigned) return;

        // A ticket_updated event can represent a handoff initiated elsewhere.
        // Once ticket data confirms the bot was unassigned, fetch the persisted
        // human conversation; machine-only updates while the bot owns the ticket
        // continue to leave the local conversation untouched.
        const updatedTicket =
          store.getState().yourSlice.supportTicketInformation;
        if (updatedTicket?.handled_by_ai_chatbot === true) {
          if (humanHandoffTicketRef.current !== ticketId) {
            handoffCommentsFetchedTicketRef.current = null;
          }
          return;
        }
        humanHandoffTicketRef.current = ticketId;
        if (
          isTicketCreationStreamInProgressRef.current ||
          isSending ||
          streamingLocalIdRef.current !== null
        ) {
          pendingHandoffCommentFetchRef.current = true;
          return;
        }
        fetchHandoffCommentsOnce(ticketId);
      });
    }

    const isAiStreamSaved = lastEvent === "ticket_ai_stream_saved" && isBot;
    const isHumanComment = lastEvent === "comment_created" && !isBot;
    if (
      isAiStreamSaved ||
      isHumanComment ||
      (lastEvent === "ticket_updated" &&
        !isBot &&
        ticketInformationRef.current?.status !== "solved")
    ) {
      // Skip entirely while ticket-creation stream is open.
      if (isTicketCreationStreamInProgressRef.current) return;

      // Normal guard: skip while a comment stream or attachment send is in flight.
      if (
        isSending ||
        (isTicketCreationStreamInProgressRef.current &&
          streamingAiReplyTextRef.current !== null) ||
        streamingLocalIdRef.current !== null
      )
        return;

      if (isBot) {
        void fetchCommentsData().then((isCurrentRequest) => {
          if (isCurrentRequest) setIsWaitingForAIResponse(false);
        });
      } else {
        void fetchCommentsData({
          preserveLocalMessages: localMessagesRef.current.length > 0,
        });
      }
    }
  }, [
    sseState,
    fetchCommentsData,
    fetchHandoffCommentsOnce,
    fetchTicketData,
    ticketId,
    isSending,
  ]);

  useEffect(() => {
    if (
      !pendingHandoffCommentFetchRef.current ||
      isSending ||
      isTicketCreationStreamInProgressRef.current ||
      streamingLocalIdRef.current !== null
    ) {
      return;
    }

    fetchHandoffCommentsOnce(ticketId!);
  }, [
    isSending,
    isStreamingAiResponse,
    streamingAiResponseText,
    localMessagesVersion,
    handoffFetchRetryVersion,
    fetchCommentsData,
    fetchHandoffCommentsOnce,
    ticketId,
  ]);

  useEffect(() => {
    preventHumanButtonRef.current = false;
  }, [ticketId]);

  useEffect(() => {
    // isWaitingForAIResponse is only ever set by an active send/stream, never
    // inferred from ticketInformation here — otherwise a ticket with an
    // unanswered message would show a permanent "Thinking..." on reopen
    // (comments are frozen while the bot owns the ticket, so nothing would
    // ever clear it).
    if (!ticketInformation?.handled_by_ai_chatbot) {
      setShowHumanSupportButton(false);
    }

    if (ticketInformation?.status === "solved") {
      setShowHumanSupportButton(false);
      setShowMarkAsDoneButton(false);
    }
  }, [ticketInformation]);

  // While bot owns the ticket, compute button thresholds from local message count
  // (no comment refetches — all messages stay local until human handoff).
  useEffect(() => {
    if (!ticketId || !ticketInformation?.handled_by_ai_chatbot) {
      return;
    }

    const allMessages = [...comments, ...localMessagesRef.current];
    const total = allMessages.length;
    const botCount = allMessages.filter((m) => m.is_ai_chatbot_comment).length;

    if (total >= 4 && botCount >= 2 && ticketInformation?.status !== "solved") {
      if (!preventHumanButtonRef.current) {
        setShowHumanSupportButton(true);
      }
      setShowMarkAsDoneButton(true);
    }
  }, [localMessagesVersion, comments, ticketInformation, ticketId]);

  const handleCloseModal = useCallback(() => {
    sendRequestIdRef.current++;
    if (ticketId) {
      clearStreamingTicketLocalFiles(ticketId);
    }
    updateLocalMessages(() => []);
    streamingLocalIdRef.current = null;
    clearStreamingAiReply();
    resetCompletedTicketCreationStream();
    dispatch(
      updateSupportCommentsByTicket({} as GetSupportCommentsByTicketResponse),
    );
    dispatch(updateSupportTicketInformation({} as supportTicketByIdType));
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "supportChatDrawerModal",
      }),
    );
  }, [
    dispatch,
    clearStreamingAiReply,
    resetCompletedTicketCreationStream,
    ticketId,
  ]);

  useEffect(
    () => () => {
      if (ticketId) {
        clearStreamingTicketLocalFiles(ticketId);
      }
    },
    [ticketId],
  );

  const sendMessage = async (
    token?: string,
    type?: string,
    onSendFailure?: () => void,
    fileName?: string,
    attachmentFile?: File,
    files?: File[],
  ) => {
    const content = input.trim();
    const attachedFiles = files ?? [];
    const hasImages = attachedFiles.length > 0;
    const isAttachment = type === "ATTACHMENT" && token;
    if (!content && !token && !hasImages) {
      onSendFailure?.();
      return;
    }
    if (isSending) {
      onSendFailure?.();
      return;
    }

    const sendRequestId = ++sendRequestIdRef.current;
    readyReceivedRef.current = false; // reset for this send — tracks if POST succeeded ("ready")
    isSendingRef.current = true;
    setIsSending(true);
    const submittedInput = input;
    setInput("");
    let sendFailureReported = false;
    const restoreComposerOnFailure = () => {
      setInput((currentInput) =>
        currentInput === "" ? submittedInput : currentInput,
      );
      if (!sendFailureReported) {
        sendFailureReported = true;
        onSendFailure?.();
      }
    };

    let optimisticHumanId: string | null = null;
    const payload: PostSupportCommentByTicketPayload = isAttachment
      ? {
          author_id: userId,
          type: "ATTACHMENT",
          upload_tokens: [token],
          text: content,
        }
      : {
          author_id: userId,
          type: "TEXT",
          text: content,
        };

    try {
      if (!isAIRagLLMBotRef.current) {
        // Human-assigned ticket — no AI stream. With images selected, use the
        // multipart with-files endpoint so the images ride along with the
        // optional file attachment; otherwise the JSON POST as before.
        if (content || isAttachment || hasImages) {
          const userLocalId = nextLocalId();
          optimisticHumanId = userLocalId;
          sentUserLocalIdRef.current = userLocalId;
          updateLocalMessages((prev) => [
            ...prev,
            {
              id: userLocalId,
              created_at: new Date().toISOString(),
              content: content || "",
              attachments: buildLocalAttachments(
                isAttachment,
                fileName,
                attachmentFile,
                attachedFiles,
              ),
              author: { id: userId, name: fullName },
            } as TicketCommentItem,
          ]);
        }
        const humanPayload = payload;

        // Ticket isn't assigned to the bot, so no AI reply will follow — use old POST.
        let result: PostSupportCommentByTicketResult;
        try {
          result = hasImages
            ? await postSupportCommentByTicketWithFilesUseCase(
                ticketId,
                humanPayload,
                attachedFiles,
              )
            : await postSupportCommentByTicketUseCase(ticketId, humanPayload);
        } catch (error) {
          if (optimisticHumanId !== null) {
            updateLocalMessages((prev) =>
              prev.filter((message) => message.id !== optimisticHumanId),
            );
          }
          restoreComposerOnFailure();
          errorSnackbar(
            error instanceof Error
              ? error.message
              : "Failed to send support message.",
          );
          return;
        }

        if (!result.success) {
          if (optimisticHumanId !== null) {
            updateLocalMessages((prev) =>
              prev.filter((message) => message.id !== optimisticHumanId),
            );
          }
          restoreComposerOnFailure();
          errorSnackbar(result.error ?? "Failed to send support message.");
          return;
        }

        await fetchCommentsData();
      } else {
        lastCommentIdBeforeSendRef.current =
          commentsRef.current[commentsRef.current.length - 1]?.id ?? null;
        // Preserve ticket-creation bubble into localMessages before clearing so the
        // greeting survives the user's first message (no re-fetch while bot assigned).
        const greetingText = streamingAiReplyTextRef.current;
        clearStreamingAiReply();
        if (greetingText) {
          const botLocalId = nextLocalId();
          updateLocalMessages((prev) => [
            ...prev,
            {
              id: botLocalId,
              created_at: new Date().toISOString(),
              content: greetingText,
              is_ai_chatbot_comment: true,
              author: {
                type: "SUPPORT_AGENT",
                id: "",
                name: "AI Assistant",
                email: null,
                organization: null,
              },
            } as TicketCommentItem,
          ]);
        }
        // Drop a completed ticket-creation handoff so comment streams stay independent.
        resetCompletedTicketCreationStream();
        // — Create a local TicketCommentItem for the user's sent message —
        // Previously this was a single string (pendingSentMessage) that got OVERWRITTEN on
        // each new send — losing history. Now we push each sent message into localMessagesRef
        // as a proper TicketCommentItem, preserving the session's conversation history.
        // These local entries stay in localMessagesRef until the ticket/modal is reset; after
        // API data includes matching comments, displayComments hides the local versions.
        if (content || isAttachment || hasImages) {
          const userLocalId = nextLocalId();
          const newMsg: TicketCommentItem = {
            id: userLocalId,
            created_at: new Date().toISOString(),
            content: content || "",
            is_ai_chatbot_comment: false,
            author: { id: userId, name: fullName },
          } as TicketCommentItem;
          sentUserLocalIdRef.current = userLocalId;
          if (isAttachment || hasImages) {
            newMsg.attachments = buildLocalAttachments(
              isAttachment,
              fileName,
              attachmentFile,
              attachedFiles,
            );
          }
          updateLocalMessages((prev) => [...prev, newMsg]);
        }
        const imageStream = hasImages
          ? postSupportCommentWithAIStreamUseCase(
              ticketId,
              payload,
              undefined,
              attachedFiles,
            )
          : null;

        try {
          for await (const event of imageStream ??
            postSupportCommentWithAIStreamUseCase(ticketId, payload)) {
            switch (event.type) {
              // — Stream "ready": POST succeeded, create empty bot entry —
              // The backend acknowledged the POST. We create an empty bot reply entry
              // (content: "") which the chat renders as "Thinking...".
              // streamingLocalIdRef tracks this entry's ID so the token handler can find
              // and update it with each new token.
              // IMPORTANT: no fetchCommentsData() here — fetching on "ready" can race
              // backend persistence. While bot is assigned the streamed reply stays in
              // localMessagesRef until reconciled via a later fetch (mount, handoff).
              case "ready": {
                if (
                  sendRequestId !== sendRequestIdRef.current ||
                  readyReceivedRef.current
                )
                  break;
                setIsWaitingForAIResponse(true);
                const botLocalId = nextLocalId();
                streamingLocalIdRef.current = botLocalId;
                readyReceivedRef.current = true; // POST succeeded — error fallback will now update the bot entry
                updateLocalMessages((prev) => [
                  ...prev,
                  {
                    id: botLocalId,
                    created_at: new Date().toISOString(),
                    content: "",
                    is_ai_chatbot_comment: true,
                    author: {
                      type: "SUPPORT_AGENT",
                      id: "",
                      name: "AI Assistant",
                      email: null,
                      organization: null,
                    },
                  } as TicketCommentItem,
                ]);
                break;
              }

              case "token":
                if (sendRequestId !== sendRequestIdRef.current) break;
                setStreamingAiReplyTools((prev) =>
                  prev.length ? completeRunningTools(prev) : prev,
                );
                updateLocalMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === streamingLocalIdRef.current
                      ? { ...msg, content: msg.content + event.text }
                      : msg,
                  ),
                );
                break;

              case "tool-call":
                if (sendRequestId !== sendRequestIdRef.current) break;
                setStreamingAiReplyTools((prev) =>
                  normalizeToolCall(event.data).reduce(mergeToolCall, prev),
                );
                break;

              case "files": {
                if (sendRequestId !== sendRequestIdRef.current) break;
                // Attach the persisted attachments (with backend thumbnails) to
                // the optimistic user message so they render and download.
                const filesEvent = event as Extract<
                  CommentStreamEvent,
                  { type: "files" }
                >;
                updateLocalMessages((prev) =>
                  prev.map((msg) => {
                    if (msg.id !== sentUserLocalIdRef.current) {
                      return msg;
                    }
                    if (filesEvent.attachments.length === 0) {
                      return msg;
                    }
                    // Keep unmatched local attachments visible when the
                    // backend skips one or returns fewer same-named files.
                    return {
                      ...msg,
                      attachments: mergeServerAttachments(
                        msg.attachments ?? [],
                        filesEvent.attachments,
                      ),
                    };
                  }),
                );
                break;
              }

              case "done":
                if (sendRequestId !== sendRequestIdRef.current) break;
                setStreamingAiReplyTools((prev) =>
                  prev.length ? completeRunningTools(prev) : prev,
                );
                setIsWaitingForAIResponse(false);
                setIsSending(false); // re-enable input; reply is already in localMessages
                streamingLocalIdRef.current = null;
                // No fallback GET here, matching the text-only comments/ai path:
                // if the "files" SSE event never arrives, thumbnails stay local
                // (local_file preview) until a later comments refetch reconciles them.
                break;

              case "error": {
                // A stale/superseded request's late error must not clobber the
                if (sendRequestId !== sendRequestIdRef.current) break;
                setStreamingAiReplyTools((prev) =>
                  prev.length ? completeRunningTools(prev) : prev,
                );
                setIsWaitingForAIResponse(false);
                // If "ready" fired, POST succeeded — update the existing bot entry with error text.
                // If "ready" did NOT fire, POST failed — push a new error entry so the user
                // sees "Sorry something went wrong" even though no bot reply was started.
                // The user's message stays in both cases (preserved in localMessagesRef).
                if (readyReceivedRef.current) {
                  updateLocalMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingLocalIdRef.current
                        ? { ...msg, content: texts.errorStreamAnswer }
                        : msg,
                    ),
                  );
                  streamingLocalIdRef.current = null;
                } else {
                  updateLocalMessages((prev) =>
                    prev.filter((msg) => msg.id !== sentUserLocalIdRef.current),
                  );
                  restoreComposerOnFailure();
                  const errorLocalId = nextLocalId();
                  updateLocalMessages((prev) => [
                    ...prev,
                    {
                      id: errorLocalId,
                      created_at: new Date().toISOString(),
                      content: texts.errorStreamAnswer,
                      is_ai_chatbot_comment: true,
                      author: {
                        type: "SUPPORT_AGENT",
                        id: "",
                        name: "AI Assistant",
                        email: null,
                        organization: null,
                      },
                    } as TicketCommentItem,
                  ]);
                }
                break;
              }
            }
          }
        } catch (err) {
          console.error("Stream error:", err);
          if (sendRequestId === sendRequestIdRef.current) {
            setStreamingAiReplyTools((prev) =>
              prev.length ? completeRunningTools(prev) : prev,
            );
            setIsWaitingForAIResponse(false);
            // Same pre/post-ready branching as the "error" event case above.
            if (readyReceivedRef.current) {
              updateLocalMessages((prev) =>
                prev.map((msg) =>
                  msg.id === streamingLocalIdRef.current
                    ? { ...msg, content: texts.errorStreamAnswer }
                    : msg,
                ),
              );
              streamingLocalIdRef.current = null;
            } else {
              updateLocalMessages((prev) =>
                prev.filter((msg) => msg.id !== sentUserLocalIdRef.current),
              );
              restoreComposerOnFailure();
              const errorLocalId = nextLocalId();
              updateLocalMessages((prev) => [
                ...prev,
                {
                  id: errorLocalId,
                  created_at: new Date().toISOString(),
                  content: texts.errorStreamAnswer,
                  is_ai_chatbot_comment: true,
                  author: {
                    type: "SUPPORT_AGENT",
                    id: "",
                    name: "AI Assistant",
                    email: null,
                    organization: null,
                  },
                } as TicketCommentItem,
              ]);
            }
          }
        }

        if (sendRequestId === sendRequestIdRef.current) {
          setIsWaitingForAIResponse(false);
        }
      }
    } finally {
      // Skip if a newer send already took over (done case sets isSending false early).
      if (sendRequestId === sendRequestIdRef.current) {
        streamingLocalIdRef.current = null;
        isSendingRef.current = false;
        setIsSending(false);
        if (
          pendingHandoffCommentFetchRef.current &&
          activeTicketIdRef.current === ticketId
        ) {
          setHandoffFetchRetryVersion((version) => version + 1);
        }
      }
    }
  };

  const refreshTicketAndComments = useCallback(async () => {
    const requestTicketId = ticketId;
    const requestGeneration = ticketRequestGenerationRef.current;
    if (!requestTicketId || activeTicketIdRef.current !== requestTicketId) {
      return;
    }

    let commentsFetched = false;
    await Promise.all([
      fetchTicketData(),
      fetchCommentsData({
        onFetchSuccess: () => {
          commentsFetched = true;
        },
      }),
    ]);
    if (
      activeTicketIdRef.current !== requestTicketId ||
      ticketRequestGenerationRef.current !== requestGeneration
    ) {
      return;
    }

    // Read from the store synchronously — after both fetches' dispatches
    // have written to Redux, auto_translate reflects the true current
    // value. The ref is stale (synced by useEffect, hasn't run yet).
    if (
      commentsFetched &&
      store.getState().yourSlice.supportTicketInformation?.auto_translate &&
      humanHandoffTicketRef.current !== requestTicketId
    ) {
      updateLocalMessages(() => []);
    }
    setIsLoading(false);
  }, [
    fetchTicketData,
    fetchCommentsData,
    updateLocalMessages,
    store,
    ticketId,
  ]);

  // Attach the ticket-creation stream's persisted images (with backend
  // thumbnails) to the first local user message once they arrive in Redux —
  // the "files" SSE event may land before or after the chat modal mounted.
  const streamingTicketAttachments = useSelector(
    (state: RootState) => state.yourSlice.streamingTicketAttachments,
  );
  const streamingTicketLocalFiles = ticketId
    ? getStreamingTicketLocalFiles(ticketId)
    : [];

  useEffect(() => {
    if (!ticketId || streamingTicketId !== ticketId) {
      return;
    }
    const localAttachments = buildLocalAttachments(
      false,
      undefined,
      undefined,
      streamingTicketLocalFiles,
    );
    const attachments = mergeServerAttachments(
      localAttachments,
      streamingTicketAttachments,
    );
    if (attachments.length === 0) {
      return;
    }

    updateLocalMessages((prev) => {
      const firstUserMessageIndex = prev.findIndex(
        (msg) => msg.author?.id === userId,
      );
      if (firstUserMessageIndex === -1) {
        return [
          ...prev,
          {
            id: nextLocalId(),
            created_at: new Date().toISOString(),
            content: "",
            attachments,
            author: { id: userId, name: fullName },
          } as TicketCommentItem,
        ];
      }

      const firstUserMessage = prev[firstUserMessageIndex];
      const hasSameAttachments =
        firstUserMessage.attachments?.length === attachments.length &&
        firstUserMessage.attachments?.every((attachment, index) => {
          const nextAttachment = attachments[index];
          return (
            attachment.id === nextAttachment?.id ||
            (attachment.file_name === nextAttachment?.file_name &&
              attachment.local_file === nextAttachment.local_file &&
              attachment.content_url === nextAttachment.content_url)
          );
        });
      if (hasSameAttachments) {
        return prev;
      }

      return prev.map((msg, index) =>
        index === firstUserMessageIndex
          ? {
              ...msg,
              attachments: mergeServerAttachments(
                msg.attachments ?? [],
                attachments,
              ),
            }
          : msg,
      );
    });
  }, [
    ticketId,
    streamingTicketId,
    streamingTicketAttachments,
    streamingTicketLocalFiles,
    updateLocalMessages,
    userId,
    localMessagesVersion,
  ]);

  // Mirror ticket-creation tokens/tools from Redux into the local bubble.
  useEffect(() => {
    if (!ticketId || streamingTicketId !== ticketId) {
      return;
    }
    if (!isStreamingAiResponse) {
      return;
    }

    updateStreamingAiReplyText(streamingAiResponseText || null);
    setStreamingAiReplyTools(streamingAiResponseTools);
    setIsWaitingForAIResponse(true);
  }, [
    ticketId,
    streamingTicketId,
    isStreamingAiResponse,
    streamingAiResponseText,
    streamingAiResponseTools,
    updateStreamingAiReplyText,
  ]);

  // When the ticket-creation stream ends, fetch comments (updates the button thresholds
  // too), then clear the bubble. Required — SSE comment_created alone isn't reliable
  // enough for this flow (buttons stayed hidden until an unrelated fetch revealed them).
  useEffect(() => {
    const isOurTicket = ticketId != null && streamingTicketId === ticketId;

    if (isOurTicket && isStreamingAiResponse) {
      wasTicketCreationStreamingRef.current = true;
      return;
    }

    if (!wasTicketCreationStreamingRef.current || isStreamingAiResponse) {
      return;
    }

    wasTicketCreationStreamingRef.current = false;

    // Error path: use-create-support-ticket-stream already reset streamingTicketId to
    // null — no reply is coming, so clear the bubble now instead of waiting forever.
    if (!isOurTicket) {
      clearStreamingAiReply();
      setIsWaitingForAIResponse(false);
      return;
    }

    // Comment stream completes tools in its done handler. Ticket-creation completes them
    // in Redux, but the mirror effect skips once isStreamingAiResponse is false — so local
    // tools can stay "running". Sync text/tools here (covers inline errors with no prior tokens).
    // Clear waiting immediately so Thinking spinner never sits on the error bubble
    // (same as AI RAG: error content + isStreaming false).
    const reduxText = streamingAiResponseTextRef.current;
    if (reduxText) {
      updateStreamingAiReplyText(reduxText);
    }
    setStreamingAiReplyTools((prev) => {
      const fromRedux = streamingAiResponseToolsRef.current;
      return completeRunningTools(fromRedux.length > 0 ? fromRedux : prev);
    });
    setIsWaitingForAIResponse(false);

    // No fetchCommentsData here — while bot is assigned all messages stay local.
    // The greeting bubble is captured into localMessagesRef on the first user send
    // (via sendMessage's greetingText capture). If it's already persisted in Redux
    // comments, clear the bubble cleanly.
    const lastComment = commentsRef.current[commentsRef.current.length - 1];
    if (lastComment?.is_ai_chatbot_comment === true) {
      finalizeBubbleIfReplyPersisted(true);
    }
    // If the "files" SSE event never arrives, image thumbnails stay local
    // (rendered via local_file) until a later comments refetch reconciles them —
    // same behavior as the comments/ai(-with-images) send path, no fallback GET here.
  }, [
    ticketId,
    streamingTicketId,
    isStreamingAiResponse,
    finalizeBubbleIfReplyPersisted,
    clearStreamingAiReply,
    updateStreamingAiReplyText,
  ]);

  useEffect(() => {
    if (!ticketId) {
      return;
    }
    sendRequestIdRef.current++;
    setInput("");
    updateLocalMessages(() => []);
    streamingLocalIdRef.current = null;
    sentUserLocalIdRef.current = null;
    isSendingRef.current = false;
    setIsSending(false);
    setIsLoading(true);
    setShowHumanSupportButton(false);
    setShowMarkAsDoneButton(false);
    setIsPatchingAssignee(false);
    setIsPatchingStatus(false);

    // Chat may open mid ticket-creation stream — keep bridged text/tools instead of wiping.
    const isJoiningTicketCreationStream =
      streamingTicketIdRef.current === ticketId &&
      (isStreamingAiResponseRef.current ||
        Boolean(streamingAiResponseTextRef.current) ||
        streamingAiResponseToolsRef.current.length > 0 ||
        streamingTicketLocalFiles.length > 0);

    if (!isJoiningTicketCreationStream) {
      clearStreamingAiReply();
      setIsWaitingForAIResponse(false);
    } else {
      updateStreamingAiReplyText(streamingAiResponseTextRef.current || null);
      setStreamingAiReplyTools(streamingAiResponseToolsRef.current);
      setIsWaitingForAIResponse(isStreamingAiResponseRef.current);
    }

    dispatch(
      updateSupportCommentsByTicket({} as GetSupportCommentsByTicketResponse),
    );

    if (isJoiningTicketCreationStream) {
      // Newly created ticket — don't GET comments (first message may not be
      // persisted yet). Write the user's initial message locally from the
      // ticket description that was set by the ticket-creation stream, with
      // its attachments built from the streamed local files and the Redux
      // attachment snapshot merged in below.
      const firstMsg = ticketInformation?.description;
      // "files" may have landed before this effect, so we snapshot the Redux
      // attachments so they aren't lost when no later effect run matches.
      const streamedAttachments =
        store.getState().yourSlice.streamingTicketAttachments;
      const localAttachments = buildLocalAttachments(
        false,
        undefined,
        undefined,
        streamingTicketLocalFiles,
      );
      const initialAttachments = mergeServerAttachments(
        localAttachments,
        streamedAttachments,
      );
      if (firstMsg || initialAttachments.length > 0) {
        const userLocalId = nextLocalId();
        updateLocalMessages((prev) => [
          ...prev,
          {
            id: userLocalId,
            created_at: new Date().toISOString(),
            content: firstMsg || "",
            is_ai_chatbot_comment: false,
            attachments:
              initialAttachments.length > 0 ? initialAttachments : undefined,
            author: { id: userId, name: fullName },
          } as TicketCommentItem,
        ]);
      }
      setIsLoading(false);
    } else {
      // Ticket data was just fetched by the taskbar card click (useClickOnCardGetTicketById)
      // and written to Redux — only comments are missing. Fetching the ticket here too
      // would fire a second identical request on every modal open.
      const requestTicketId = ticketId;
      const requestGeneration = ticketRequestGenerationRef.current;
      void fetchCommentsData().then((isCurrentRequest) => {
        if (
          isCurrentRequest &&
          activeTicketIdRef.current === requestTicketId &&
          ticketRequestGenerationRef.current === requestGeneration
        ) {
          setIsLoading(false);
        }
      });
    }
  }, [
    ticketId,
    dispatch,
    updateStreamingAiReplyText,
    clearStreamingAiReply,
    fetchCommentsData,
  ]);

  useEffect(() => {
    if (previousLanguageRef.current === language) {
      return;
    }

    previousLanguageRef.current = language;

    if (!ticketInformation?.auto_translate) {
      return;
    }

    void refreshTicketAndComments();
  }, [language, refreshTicketAndComments, ticketInformation?.auto_translate]);

  return {
    ticketInformation,
    comments: displayComments,
    isLoading,
    input,
    setInput,
    isSending,
    sendMessage,
    refreshTicketAndComments,
    refreshTicketData: fetchTicketData,
    handleCloseModal,
    isWaitingForAIResponse,
    userId,
    streamingAiReplyText,
    streamingAiReplyTools,
    showHumanSupportButton,
    showMarkAsDoneButton,
    patchTicketAssignee,
    patchTicketMarkAsDone,
    isPatchingAssignee,
    isPatchingStatus,
    isSSEUnavailable: !sseState?.isConnected,
  };
};
